import i18nFactory, { i18nRaw as i18nRawFactory } from '@yandex-int/i18n';
import classNames from 'classnames';
import { Select } from 'lego-on-react';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { parseWithRound } from '../../service/FormatHelpers/FormatHelpers';
import { TestBem } from '../../service/TestBem';
import { ValidationErrorData } from '../../typings/common';
import {
    RateControlMode,
    RateControlValue
} from '../../typings/ratesCorrections';
import { DirectFormInput } from '../DirectFormInput/DirectFormInput';
import { ErrorText, ErrorTextTheme } from '../ErrorText/ErrorText';
import { InputFieldType } from '../InputField/InputField';

import './RateControl.css';
import * as keyset from './RateControl.i18n';

const i18n = i18nFactory(keyset);
const i18nRaw = i18nRawFactory(keyset);

interface RateControlProps {
    cls?: string;
    value: RateControlValue;
    min?: number;
    max?: number;
    isDisabled?: boolean;
    isCommon: boolean;
    onChange: (value: RateControlValue) => void;
    errors?: ValidationErrorData[];
}

interface RateControlState {
    inputValue: string;
}

const rateControlModeTypes = {
    [RateControlMode.up]: {
        icon: '↑',
        text: i18n('up')
    },
    [RateControlMode.down]: {
        icon: '↓',
        text: i18n('down')
    },
    [RateControlMode.none]: {
        icon: '—',
        text: ''
    }
};

const blockClass = 'rate-control';

export class RateControl extends React.PureComponent<
    RateControlProps,
    RateControlState
> {
    static getStringFromValue(value: RateControlValue) {
        return value.value !== undefined ? value.value.toString() : '';
    }

    componentDidUpdate(prevProps: RateControlProps) {
        const newValueString = RateControl.getStringFromValue(this.props.value);
        const oldValueString = RateControl.getStringFromValue(prevProps.value);

        if (newValueString !== oldValueString) {
            this.setState({
                inputValue: newValueString
            });
        }
    }

    constructor(props: RateControlProps) {
        super(props);

        this.state = {
            inputValue: RateControl.getStringFromValue(props.value)
        };
    }

    render() {
        const { cls, value, min, max, errors, isDisabled } = this.props;
        const { inputValue } = this.state;

        const modeToPlaceholder = {
            [RateControlMode.none]: '',
            [RateControlMode.up]: max ? `max ${max}` : '',
            [RateControlMode.down]: min ? `max ${Math.abs(min)}` : ''
        };

        const selectedModeItem = this.getItemForSelectMode(
            rateControlModeTypes[value.mode].icon,
            rateControlModeTypes[value.mode].text,
            true
        );

        return (
            <div className={classNames(blockClass, cls)}>
                <Select
                    cls={classNames(
                        Bem.cls(blockClass, 'mode'),
                        TestBem.cls(blockClass, 'mode')
                    )}
                    theme="normal"
                    size="s"
                    type="radio"
                    onChange={this.onSelectChange}
                    button={{
                        children: selectedModeItem,
                        text: undefined
                    }}
                    val={value.mode}
                    disabled={isDisabled}
                    popup={{
                        hiding: false
                    }}
                    items={this.getModeSelectItems()}
                />

                <div className={Bem.cls(blockClass, 'at-percent')}>
                    {i18nRaw('at-percent', {
                        conversionInput: (
                            <DirectFormInput
                                disabled={isDisabled}
                                inputType={InputFieldType.TEXT}
                                theme="normal"
                                size="s"
                                view="classic"
                                placeholder={
                                    value.mode
                                        ? modeToPlaceholder[value.mode]
                                        : ''
                                }
                                cls={classNames(
                                    Bem.cls(blockClass, 'value'),
                                    TestBem.cls(blockClass, 'value')
                                )}
                                text={inputValue}
                                onChange={this.onInputChange}
                                onBlur={this.onInputBlur}
                                hasClear={false}
                                valid={
                                    errors === undefined || errors.length === 0
                                }
                                errorText={
                                    <ErrorText
                                        theme={ErrorTextTheme.tooltip}
                                        errors={errors}
                                    />
                                }
                            />
                        )
                    })}
                </div>
            </div>
        );
    }

    private getModeSelectItems = () => {
        const { isCommon } = this.props;

        return [
            RateControlMode.none,
            RateControlMode.up,
            RateControlMode.down
        ].reduce((acc: React.ReactNode[], key) => {
            if (
                key !== RateControlMode.none ||
                (key === RateControlMode.none && isCommon)
            ) {
                acc.push({
                    cls: TestBem.cls(blockClass, 'mode-item', {
                        [key]: true
                    }),
                    text: this.getItemForSelectMode(
                        rateControlModeTypes[key].icon,
                        rateControlModeTypes[key].text,
                        false
                    ),
                    val: key
                });
            }

            return acc;
        }, []);
    };

    private getItemForSelectMode = (
        icon: string,
        text?: string,
        isButtonControl?: boolean
    ): React.ReactNode => {
        return (
            <span
                className={Bem.cls(blockClass, 'mode-select', {
                    button: isButtonControl
                })}
            >
                <span className={Bem.cls(blockClass, 'mode-select-icon')}>
                    {icon}
                </span>
                {text ? (
                    <span className={Bem.cls(blockClass, 'mode-select-text')}>
                        {text}
                    </span>
                ) : (
                    undefined
                )}
            </span>
        );
    };

    private onInputChange = (value: string) => {
        this.setState({
            inputValue: value
        });
    };

    private onInputBlur = () => {
        const { inputValue } = this.state;
        const { value, onChange } = this.props;
        const parsedValue = parseWithRound(inputValue);

        if (
            inputValue.trim() !== '' &&
            parsedValue !== undefined &&
            parsedValue >= 0
        ) {
            this.onInputChange(parsedValue.toString());

            return onChange({
                ...value,
                value: parsedValue
            });
        }

        this.onInputChange('');

        return onChange({
            ...value,
            value: undefined
        });
    };

    private onSelectChange = (values: Array<RateControlMode>) => {
        const { onChange, value } = this.props;

        onChange({
            ...value,
            mode: values[0]
        });
    };
}
